/**
 * Mete (Livello 1) — orizzonti 1/5/10 anni · Blocco 4 · F · Lifecycle completo.
 *
 * Layout:
 *   - Tab primario stato: Attive · Raggiunte · Annullate
 *   - Tab secondario orizzonte (dentro Attive): 1a / 5a / 10a
 *   - Card meta con badge stato + progress cascade
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Target, Plus, X, Check, Award, RotateCcw, ChevronRight,
  AlertTriangle, Zap, Ban,
} from "lucide-react";
import { api } from "../lib/api";
import Tip from "../components/Tip";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import DatePickerIT from "../components/ui/date-picker-it";
import MetaAchieveModal from "../components/MetaAchieveModal";
import MetaCancelModal from "../components/MetaCancelModal";
import { celebrate } from "../lib/confetti";

const HORIZONS = [
  { v: "1_year",   label: "1 anno",  color: "#8FBF8F" },
  { v: "5_years",  label: "5 anni",  color: "#F5C24A" },
  { v: "10_years", label: "10 anni", color: "#C3DAF4" },
];

const STATUS_TABS = [
  { v: "active",    label: "Attive" },
  { v: "achieved",  label: "Raggiunte" },
  { v: "cancelled", label: "Annullate" },
];

export default function Mete() {
  const [statusTab, setStatusTab] = useState("active");
  const [horizon, setHorizon] = useState("1_year");
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [achieving, setAchieving] = useState(null);
  const [cancelling, setCancelling] = useState(null);

  const load = () => {
    setLoading(true);
    // For 'active' tab we filter by horizon; for others we show all horizons.
    const params = statusTab === "active"
      ? { horizon, status: "active" }
      : { status: statusTab };
    api.listGoalsWithProgress(params)
      .then((g) => { setGoals(g || []); setLoading(false); })
      .catch(() => { toast.error("Errore caricamento mete"); setLoading(false); });
  };
  useEffect(() => { load(); }, [statusTab, horizon]); // eslint-disable-line

  const active = useMemo(() => goals.filter((g) => g.status === "active"), [goals]);
  const achieved = useMemo(() => goals.filter((g) => g.status === "achieved"), [goals]);
  const cancelled = useMemo(() => goals.filter((g) => g.status === "cancelled"), [goals]);

  return (
    <div className="space-y-5" data-testid="mete-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest opacity-70">Il perché</div>
          <h1 className="mt-1 font-display text-3xl sm:text-4xl font-semibold inline-flex items-center gap-2">
            <Target size={26}/> Mete
          </h1>
          <p className="mt-2 text-sm opacity-80 max-w-2xl">
            Impegni con te stessa a <b>1, 5 e 10 anni</b>. Il progresso arriva
            dalle <Link to="/azioni" className="underline">Azioni</Link> collegate.
          </p>
        </div>
        <Tip label="Crea una nuova meta">
          <button onClick={() => setShowCreate(true)} className="pxos-btn-primary text-sm" data-testid="mete-new">
            <Plus size={14}/> Nuova meta
          </button>
        </Tip>
      </div>

      {/* Primary status tabs */}
      <Tabs value={statusTab} onValueChange={setStatusTab} data-testid="mete-status-tabs">
        <TabsList className="w-full sm:w-auto">
          {STATUS_TABS.map((t) => (
            <TabsTrigger key={t.v} value={t.v} data-testid={`mete-status-${t.v}`}>
              {t.label}
              {t.v === "active" && active.length > 0 && (
                <span className="ml-1.5 text-[10px] opacity-60">·{active.length}</span>
              )}
              {t.v === "achieved" && achieved.length > 0 && (
                <span className="ml-1.5 text-[10px] opacity-60">·{achieved.length}</span>
              )}
              {t.v === "cancelled" && cancelled.length > 0 && (
                <span className="ml-1.5 text-[10px] opacity-60">·{cancelled.length}</span>
              )}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* Attive */}
        <TabsContent value="active" className="mt-4 space-y-4">
          <Tabs value={horizon} onValueChange={setHorizon} data-testid="mete-horizon-tabs">
            <TabsList className="w-full sm:w-auto">
              {HORIZONS.map((h) => (
                <TabsTrigger key={h.v} value={h.v} data-testid={`mete-tab-${h.v}`}>{h.label}</TabsTrigger>
              ))}
            </TabsList>
            {HORIZONS.map((h) => (
              <TabsContent key={h.v} value={h.v} className="mt-3 space-y-2">
                {loading ? (
                  <div className="opacity-60 text-sm py-10 text-center">Caricamento…</div>
                ) : active.length === 0 ? (
                  <Empty msg={`Nessuna meta a ${h.label.toLowerCase()} attiva. Crea la prima per iniziare.`}/>
                ) : (
                  active.map((g) => (
                    <GoalCard key={g.id} goal={g} accent={h.color}
                      onAchieve={() => setAchieving(g)}
                      onCancel={() => setCancelling(g)}/>
                  ))
                )}
              </TabsContent>
            ))}
          </Tabs>
        </TabsContent>

        {/* Raggiunte */}
        <TabsContent value="achieved" className="mt-4 space-y-2">
          {loading ? (
            <div className="opacity-60 text-sm py-10 text-center">Caricamento…</div>
          ) : achieved.length === 0 ? (
            <Empty msg="Nessuna meta raggiunta al momento."/>
          ) : (
            achieved.map((g) => (
              <GoalCard key={g.id} goal={g} accent="#8FBF8F"
                onRestore={() => restoreAndReload(g.id)} />
            ))
          )}
        </TabsContent>

        {/* Annullate */}
        <TabsContent value="cancelled" className="mt-4 space-y-2">
          {loading ? (
            <div className="opacity-60 text-sm py-10 text-center">Caricamento…</div>
          ) : cancelled.length === 0 ? (
            <Empty msg="Nessuna meta annullata."/>
          ) : (
            cancelled.map((g) => (
              <GoalCard key={g.id} goal={g} accent="#9CA3AF"
                onRestore={() => restoreAndReload(g.id)} />
            ))
          )}
        </TabsContent>
      </Tabs>

      {showCreate && <CreateModal defaultHorizon={horizon} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }}/>}
      {achieving && (
        <MetaAchieveModal
          entity={{ title: achieving.title }}
          onClose={() => setAchieving(null)}
          onConfirm={async ({ achieved_date, achieved_notes }) => {
            await api.achieveGoal(achieving.id, { achieved_date, achieved_notes });
            setAchieving(null); load(); celebrate({ durationMs: 2000 });
            toast.success("🎉 Meta raggiunta");
          }}/>
      )}
      {cancelling && (
        <MetaCancelModal
          entity={{ title: cancelling.title }}
          onClose={() => setCancelling(null)}
          onConfirm={async ({ reason }) => {
            await api.cancelGoal(cancelling.id, reason);
            setCancelling(null); load();
            toast.success("Meta annullata");
          }}/>
      )}
    </div>
  );

  async function restoreAndReload(id) {
    try { await api.restoreGoal(id); toast.success("Meta riportata attiva"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  }
}

function Empty({ msg }) {
  return <div className="pxos-card p-6 text-sm opacity-60 text-center">{msg}</div>;
}

function GoalCard({ goal, accent, onAchieve, onCancel, onRestore }) {
  const pct = Number(goal.percent_complete || 0);
  const barColor = pct >= 100 ? "#3F8F5C" : pct >= 60 ? "#8FBF8F" : pct >= 30 ? "#F5C24A" : "#95BAEF";
  const dateFmt = (iso) => iso ? new Date(iso).toLocaleDateString("it-IT") : null;

  return (
    <Link to={`/mete/${goal.id}`}
          className="block border rounded-xl p-3.5 bg-white hover:shadow-sm transition"
          style={{ borderColor: "var(--pxos-border)", borderLeft: `4px solid ${accent}` }}
          data-testid="goal-card">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-display text-base font-semibold truncate flex-1" data-testid="goal-title">{goal.title}</div>
            {goal.status === "achieved" && (
              <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full inline-flex items-center gap-1"
                    style={{ background: "#DCFCE7", color: "#166534" }}
                    data-testid="goal-badge-achieved">
                <Check size={9}/> Raggiunta{dateFmt(goal.achieved_at) ? ` · ${dateFmt(goal.achieved_at)}` : ""}
              </span>
            )}
            {goal.status === "cancelled" && (
              <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full inline-flex items-center gap-1"
                    style={{ background: "#F3F4F6", color: "#4B5563" }}
                    data-testid="goal-badge-cancelled">
                <Ban size={9}/> Annullata{dateFmt(goal.cancelled_at) ? ` · ${dateFmt(goal.cancelled_at)}` : ""}
              </span>
            )}
          </div>
          {goal.category && <div className="text-[11px] opacity-60 mt-0.5">{goal.category}</div>}
          {goal.description && <div className="text-xs opacity-70 mt-1 line-clamp-2">{goal.description}</div>}
          {goal.status === "cancelled" && goal.cancelled_reason && (
            <div className="text-xs opacity-70 mt-1 italic line-clamp-2" data-testid="goal-cancel-reason">
              Motivo: {goal.cancelled_reason}
            </div>
          )}
        </div>
        <ChevronRight size={16} className="opacity-40 shrink-0 mt-1"/>
      </div>

      {goal.status !== "cancelled" && (
        <div className="mt-3">
          <div className="flex items-center justify-between text-[11px] opacity-70 mb-1">
            <span className="inline-flex items-center gap-1">
              <Zap size={11}/> Da {goal.azioni_count || 0} azion{goal.azioni_count === 1 ? "e" : "i"}
            </span>
            <span className="font-mono font-semibold" data-testid="goal-progress-pct">{pct.toFixed(0)}%</span>
          </div>
          <div className="h-2 rounded-full overflow-hidden bg-black/5">
            <div className="h-full transition-all"
                 style={{ width: `${Math.min(100, pct)}%`, background: barColor }}
                 data-testid="goal-progress-bar"/>
          </div>
        </div>
      )}

      <div className="mt-3 flex items-center justify-between">
        <div className="text-[11px] opacity-60">
          {goal.target_date && <>Target: {new Date(goal.target_date).toLocaleDateString("it-IT")}</>}
        </div>
        <div className="flex gap-1" onClick={(e) => e.preventDefault()}>
          {goal.status === "active" && onAchieve && (
            <button onClick={(e) => { e.preventDefault(); onAchieve(); }}
                    className="rounded-full border px-2.5 py-1 text-[11px] inline-flex items-center gap-1"
                    style={{ borderColor: "#8FBF8F", color: "#2F6B3F" }}
                    data-testid="goal-mark-achieved">
              <Award size={11}/> Raggiunta
            </button>
          )}
          {goal.status === "active" && onCancel && (
            <button onClick={(e) => { e.preventDefault(); onCancel(); }}
                    className="rounded-full border px-2.5 py-1 text-[11px] opacity-70"
                    data-testid="goal-cancel">
              Annulla
            </button>
          )}
          {goal.status !== "active" && onRestore && (
            <button onClick={(e) => { e.preventDefault(); onRestore(); }}
                    className="rounded-full border px-2.5 py-1 text-[11px] inline-flex items-center gap-1"
                    data-testid="goal-restore">
              <RotateCcw size={11}/> Ripristina
            </button>
          )}
        </div>
      </div>
    </Link>
  );
}

function CreateModal({ defaultHorizon, onClose, onCreated }) {
  const [form, setForm] = useState({
    horizon: defaultHorizon, title: "", description: "", category: "", target_date: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (form.title.trim().length < 3) { toast.error("Il titolo deve avere almeno 3 caratteri"); return; }
    setBusy(true);
    try {
      await api.createGoal({
        horizon: form.horizon,
        title: form.title,
        description: form.description || null,
        category: form.category || null,
        target_date: form.target_date || null,
      });
      toast.success("Meta creata · impegno registrato");
      onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="goal-create-modal">
      <div className="bg-white rounded-2xl max-w-lg w-full p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl font-semibold">Nuova meta</h3>
          <button onClick={onClose} data-testid="goal-modal-close" aria-label="Chiudi"><X size={20}/></button>
        </div>
        <label className="block text-sm">
          <div className="opacity-70 mb-1">Orizzonte</div>
          <div className="inline-flex rounded-full border overflow-hidden">
            {HORIZONS.map((h) => (
              <button key={h.v}
                onClick={() => setForm({ ...form, horizon: h.v })}
                className={`px-3 py-1.5 text-xs ${form.horizon === h.v ? "bg-white shadow-inner font-semibold" : "opacity-70"}`}
                data-testid={`goal-horizon-${h.v}`}>
                {h.label}
              </button>
            ))}
          </div>
        </label>
        <label className="block text-sm">
          <div className="opacity-70 mb-1">Titolo *</div>
          <input className="pxos-input" value={form.title} maxLength={200}
                 onChange={(e) => setForm({ ...form, title: e.target.value })}
                 data-testid="goal-title-input"/>
        </label>
        <label className="block text-sm">
          <div className="opacity-70 mb-1">Descrizione</div>
          <textarea className="pxos-input" rows={3} value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    data-testid="goal-description-input"/>
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block text-sm">
            <div className="opacity-70 mb-1">Categoria</div>
            <input className="pxos-input" value={form.category} maxLength={80}
                   onChange={(e) => setForm({ ...form, category: e.target.value })}
                   placeholder="es. Salute, Business…"
                   data-testid="goal-category-input"/>
          </label>
          <label className="block text-sm">
            <div className="opacity-70 mb-1">Data target</div>
            <DatePickerIT value={form.target_date}
                          onChange={(v) => setForm({ ...form, target_date: v })}
                          testId="goal-target-date" placeholder="gg/mm/aaaa"/>
          </label>
        </div>
        <div className="flex items-start gap-2 rounded-md p-2 text-[11px]" style={{ background: "#FFF8E1", color: "#8B6D0A" }}>
          <AlertTriangle size={14} className="shrink-0 mt-0.5"/>
          <div>Puoi <b>modificare</b> questa meta anche dopo. Se cambia il vento, non serve buttarla via.</div>
        </div>
        <div className="flex gap-2 pt-2 justify-end">
          <button onClick={onClose} className="text-sm underline opacity-70">Annulla</button>
          <button disabled={busy} onClick={submit} className="pxos-btn-primary text-sm" data-testid="goal-create-submit">
            <Check size={14}/> Sono pronta, crea
          </button>
        </div>
      </div>
    </div>
  );
}
